import Modal from '@/components/shared/modal.component';
import Typography from '@/components/shared/typography/typography.component';
import { sylndrDirections } from '@/constants';
import { useTranslation } from '@/hook';
import myImageLoader from '@/image.loader';
import { useAnalytics } from '@/integrations';
import { BookingTypes, EInspectionType, ReserveTypes } from '@/types';
import Image from 'next/image';
import Link from 'next/link';
import {
    BookingCallIcon,
    BookingClockIcon,
    BookingLocationIcon,
    CloseButtonIcon,
    MyBookingCoverPhoto,
    WhiteNavigatorIcon,
} from 'public/Images';
import { FC } from 'react';

interface SylndrDirectionModalProps {
    open: boolean;
    closeModal: () => void;
    title: string;
    bookingLocation: string;
    reserveType: ReserveTypes;
    bookingType: BookingTypes;
}
const Divider = () => {
    return <div className="w-full h-[1px] bg-gray-200 my-4" />;
};
const sellPageSource = 'Sell Page';
const SylndrDirectionsModal: FC<SylndrDirectionModalProps> = ({
    open,
    closeModal,
    title,
    bookingLocation,
    reserveType,
    bookingType,
}) => {
    const { t } = useTranslation();
    const { trackEvent } = useAnalytics();
    const IsHub =
        reserveType === ReserveTypes.SHOWROOM ||
        (bookingType == BookingTypes.SEE && !reserveType);
    const imageLink = myImageLoader({
        src: MyBookingCoverPhoto.src,
        width: 955,
        height: 500,
        quality: 90,
    });
    return (
        <Modal
            PapperProps={{ borderRadius: '16px', height: 'max-content' }}
            open={open}
            setOpen={closeModal}
        >
            <div className="w-screen h-max  md:w-[375px] bg-white flex-col flex rounded-2xl">
                <div className="flex justify-start items-center py-4 px-4">
                    <Typography
                        variant="subtitle2SemiBold"
                        className="!text-gray-800"
                    >
                        {title}
                        <span className="ar:hidden">
                            {IsHub && `, ${t('katameya')}`}
                        </span>
                        <span className="en:hidden">
                            {IsHub && ` , ${t('katameya')}`}
                        </span>
                    </Typography>

                    <Image
                        onClick={closeModal}
                        alt="close"
                        src={CloseButtonIcon}
                        className="ar:mr-auto en:ml-auto cursor-pointer"
                    ></Image>
                </div>
                <div className="w-full flex flex-col py-2 px-4">
                    {IsHub && (
                        <img
                            alt="sylndr hub"
                            src={imageLink}
                            className="w-full h-[140px] object-cover  rounded-lg mb-6"
                        />
                    )}
                    <div className="flex flex-row items-start gap-2">
                        <Image alt="clock" src={BookingClockIcon} width={18} />
                        <div className="flex gap-2 flex-col">
                            <Typography
                                variant="body2SemiBold"
                                className="!text-gray-700"
                            >
                                {t('Working Hours')}
                            </Typography>
                            <Typography
                                variant="captionMedium"
                                className="!text-gray-600"
                            >
                                {t('SalesWorkingHours')}
                            </Typography>
                        </div>
                    </div>
                    <Divider />
                    <div className="flex flex-row items-start gap-2 md:mb-0 mb-8 ">
                        <Image
                            alt="clock"
                            src={BookingLocationIcon}
                            className="ar:w-5 en:w-7"
                        />
                        <div className="flex gap-2 flex-col">
                            <Typography
                                variant="body2SemiBold"
                                className="!text-gray-700"
                            >
                                {IsHub && t('hubAddress')}
                                {!IsHub && t('visitDetails.location')}
                            </Typography>
                            <Typography
                                variant="captionMedium"
                                className="!text-gray-600"
                            >
                                {IsHub && t('mainShowroomAddress')}
                                {!IsHub && bookingLocation}
                            </Typography>
                            {IsHub && (
                                <div className="w-full p-2 bg-gray-50  border border-gray-200 rounded-md">
                                    <Typography className="!text-[10px] !text-gray-600 !font-normal">
                                        {bookingLocation}
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </div>
                    {!IsHub && (
                        <div className="w-full h-[1px] bg-gray-200 absolute bottom-6 left-0 md:hidden"></div>
                    )}

                    <Divider />
                    <div className="flex flex-row items-start gap-2">
                        <Image alt="clock" src={BookingCallIcon} width={18} />
                        <div
                            className={`flex gap-2 flex-col ${
                                !IsHub && 'mb-2'
                            }`}
                        >
                            <Typography
                                variant="body2SemiBold"
                                className="!text-gray-700"
                            >
                                {t('sylndrHotline')}
                            </Typography>
                            <Typography
                                variant="captionMedium"
                                className="!text-gray-600"
                            >
                                17188
                            </Typography>
                        </div>
                    </div>
                </div>
                {IsHub && (
                    <div className="py-3  W-full px-4 border-t-gray-200 border-t">
                        <Link
                            prefetch={false}
                            href={sylndrDirections}
                            target="_blank"
                            className="flex justify-center items-center gap-2 !w-full bg-primary-600 rounded-lg h-12 "
                            onClick={() => {
                                trackEvent('Action: Sell_Map_Icon', {
                                    inspectionType: EInspectionType.WAREHOUSE,
                                    sellPageSource,
                                });
                            }}
                        >
                            <Image
                                src={WhiteNavigatorIcon}
                                width={18}
                                alt="navigator"
                            />
                            <Typography
                                variant="body2Medium"
                                className="!text-white"
                            >
                                {t('directions')}

                            </Typography>
                        </Link>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default SylndrDirectionsModal;
